import React, { useState, useEffect, useRef } from 'react';
import { Box, Paper, Typography, Button, Alert, Tabs, Tab, CircularProgress } from '@mui/material';
import { PlayArrow, Refresh } from '@mui/icons-material';
import Editor from '@monaco-editor/react';
import { transform } from '@babel/standalone';

function ReactWorkspace({ question, onCodeChange, initialCode = '' }) {
  const defaultCode = `import React from 'react';

function App() {
  const [count, setCount] = React.useState(0);

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial' }}>
      <h1>React Component</h1>
      <p>Count: {count}</p>
      <button onClick={() => setCount(count + 1)}>
        Increment
      </button>
    </div>
  );
}

export default App;`;

  const [code, setCode] = useState(initialCode || defaultCode);
  const [compiledCode, setCompiledCode] = useState('');
  const [error, setError] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [previewKey, setPreviewKey] = useState(0);
  const iframeRef = useRef(null);
  const editorRef = useRef(null);

  useEffect(() => {
    if (onCodeChange) {
      onCodeChange(code);
    }
  }, [code, onCodeChange]);

  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;

    // Configure TypeScript/JSX support
    monaco.languages.typescript.typescriptDefaults.setCompilerOptions({
      target: monaco.languages.typescript.ScriptTarget.ES2020,
      allowNonTsExtensions: true,
      moduleResolution: monaco.languages.typescript.ModuleResolutionKind.NodeJs,
      module: monaco.languages.typescript.ModuleKind.ESNext,
      jsx: monaco.languages.typescript.JsxEmit.React,
      esModuleInterop: true,
      allowJs: true,
    });
  };

  const compileAndRun = async () => {
    setIsRunning(true);
    setError('');
    setActiveTab(1); // Switch to preview tab

    try {
      // Transform JSX to JavaScript using Babel
      const result = transform(code, {
        presets: ['react'],
        filename: 'App.jsx',
      });

      setCompiledCode(result.code);

      // Create iframe content with React runtime
      const iframeContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <script crossorigin src="https://unpkg.com/react@18/umd/react.development.js"></script>
  <script crossorigin src="https://unpkg.com/react-dom@18/umd/react-dom.development.js"></script>
  <style>
    body { margin: 0; padding: 0; font-family: Arial, sans-serif; }
    #root { width: 100%; height: 100vh; }
  </style>
</head>
<body>
  <div id="root"></div>
  <script>
    try {
      ${result.code}

      // Render the component
      const root = ReactDOM.createRoot(document.getElementById('root'));
      root.render(React.createElement(App));
    } catch (err) {
      document.body.innerHTML = '<div style="padding: 20px; color: red;"><h3>Runtime Error:</h3><pre>' + err.toString() + '</pre></div>';
      console.error(err);
    }
  </script>
</body>
</html>`;

      // Update iframe
      if (iframeRef.current) {
        const doc = iframeRef.current.contentDocument || iframeRef.current.contentWindow.document;
        doc.open();
        doc.write(iframeContent);
        doc.close();
      }

      setPreviewKey(prev => prev + 1);
    } catch (err) {
      setError('Compilation Error: ' + err.message);
      console.error('Babel compilation error:', err);
    } finally {
      setIsRunning(false);
    }
  };

  const refresh = () => {
    setPreviewKey(prev => prev + 1);
    compileAndRun();
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Question Display */}
      {question && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            {question.name || 'React Challenge'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
            {question.task || question.question || question.text}
          </Typography>
        </Paper>
      )}

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="React/JSX Editor" />
        <Tab label="Live Preview" />
      </Tabs>

      {/* Editor Tab */}
      {activeTab === 0 && (
        <Box sx={{ flexGrow: 1, border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden' }}>
          <Editor
            height="100%"
            defaultLanguage="javascript"
            path="App.jsx"
            value={code}
            onChange={(value) => setCode(value || '')}
            onMount={handleEditorDidMount}
            theme="vs-dark"
            options={{
              minimap: { enabled: false },
              fontSize: 14,
              lineNumbers: 'on',
              roundedSelection: false,
              scrollBeyondLastLine: false,
              automaticLayout: true,
              tabSize: 2,
            }}
          />
        </Box>
      )}

      {/* Preview Tab */}
      {activeTab === 1 && (
        <Box sx={{ flexGrow: 1, border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden', bgcolor: 'white' }}>
          <iframe
            key={previewKey}
            ref={iframeRef}
            title="React Preview"
            style={{ width: '100%', height: '100%', border: 'none' }}
            sandbox="allow-scripts"
          />
        </Box>
      )}

      {/* Action Buttons */}
      <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={isRunning ? <CircularProgress size={20} color="inherit" /> : <PlayArrow />}
          onClick={compileAndRun}
          disabled={isRunning}
        >
          {isRunning ? 'Compiling...' : 'Run & Preview'}
        </Button>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={refresh}
          disabled={isRunning}
        >
          Refresh Preview
        </Button>
      </Box>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mt: 2 }}>
        <strong>React Workspace</strong> - Write React components with JSX.
        Components are compiled with Babel and rendered in real-time.
      </Alert>
    </Box>
  );
}

export default ReactWorkspace;
